/**
 * Attachments, the pure part (docs/attachments-plan.md): what a file may be,
 * told by its first bytes rather than its name — images and PDFs, up to
 * MAX_SIZE, kept as they are (a receipt must stay readable) — and its hash.
 */

/** The largest file that may be attached. */
export const MAX_SIZE = 20 * 1024 * 1024

const ascii = (bytes, from, to) => String.fromCharCode(...bytes.subarray(from, to))

/**
 * The file's type from its first bytes — `image/jpeg`, `image/png`,
 * `image/gif`, `image/webp`, `image/heic`, `image/avif` or `application/pdf` —
 * or null for anything else, whatever its name says.
 */
export function sniffType(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  if (b.length < 12) return null
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg'
  if (b[0] === 0x89 && ascii(b, 1, 4) === 'PNG') return 'image/png'
  if (ascii(b, 0, 4) === 'GIF8') return 'image/gif'
  if (ascii(b, 0, 4) === 'RIFF' && ascii(b, 8, 12) === 'WEBP') return 'image/webp'
  if (ascii(b, 0, 5) === '%PDF-') return 'application/pdf'
  // ISO media (a phone's HEIC/HEIF, or AVIF): `ftyp` and the brand.
  if (ascii(b, 4, 8) === 'ftyp') {
    const brand = ascii(b, 8, 12)
    if (brand === 'avif' || brand === 'avis') return 'image/avif'
    if (['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1'].includes(brand)) return 'image/heic'
  }
  return null
}

/** Whether a browser can draw it as an <img> — HEIC mostly cannot, so it is offered as a download. */
export const showable = (type) => /^image\/(jpeg|png|gif|webp|avif)$/.test(type)

/** SHA-256 of the bytes, as hex. */
export async function sha256Hex(bytes) {
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))
  return [...hash].map((x) => x.toString(16).padStart(2, '0')).join('')
}

/**
 * Checks a file about to be attached: `{ type }`, or throws `attachment-type`
 * (not an image or a PDF) / `attachment-too-big`.
 */
export function vet(bytes) {
  if (bytes.byteLength > MAX_SIZE) throw Object.assign(new Error('That file is too big to attach.'), { code: 'attachment-too-big' })
  const type = sniffType(bytes)
  if (!type) throw Object.assign(new Error('Only images and PDFs can be attached.'), { code: 'attachment-type' })
  return { type }
}

/** Bytes as people read them: 820 KB, 3.4 MB. */
export function sizeLabel(size, locale) {
  const nf = (n, digits) => new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(n)
  if (size < 1024 * 1024) return `${nf(Math.max(1, Math.round(size / 1024)), 0)} KB`
  return `${nf(size / (1024 * 1024), 1)} MB`
}
